import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { imgSrc } from '../lib/imgSrc';

// Sentinel option values (kept off any real IDs).
const UNASSIGNED_LOC = '__unassigned__';
const OTHER_EQUIPMENT = '__other__';

/**
 * Two-step "Submit Maintenance Request" dialog.
 *
 * Step 1 mirrors the Maintenance Log's equipment picker:
 *   Location select  → includes "Unassigned Equipment" as a real option
 *   Equipment select → alphabetized machinery for the chosen location, plus
 *                      an "Other — please specify" fallback that reveals a
 *                      free-text field
 */
const SubmitMaintenanceRequestDialog = ({ open, onClose, defaultLocationId = '', onSuccess }) => {
  const [locations, setLocations] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  const [step, setStep] = useState(1);
  const [locationChoice, setLocationChoice] = useState(defaultLocationId || '');
  const [equipmentChoice, setEquipmentChoice] = useState('');
  const [otherEquipmentName, setOtherEquipmentName] = useState('');

  const [description, setDescription] = useState('');
  const [observedAt, setObservedAt] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setLocationChoice(defaultLocationId || '');
    setEquipmentChoice('');
    setOtherEquipmentName('');
    setDescription('');
    setImageFiles([]);
    setImagePreviews([]);
    (async () => {
      try {
        const res = await axios.get(`${API}/locations`);
        // Alphabetize location list.
        setLocations([...(res.data || [])].sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        toast.error('Could not load plant locations');
      }
    })();
  }, [open, defaultLocationId]);

  // Load equipment for the chosen location (or unassigned bucket).
  useEffect(() => {
    if (!locationChoice) {
      setMachinery([]);
      setEquipmentChoice('');
      return;
    }
    let aborted = false;
    setLoadingEquipment(true);
    setEquipmentChoice('');
    setOtherEquipmentName('');
    (async () => {
      try {
        const url =
          locationChoice === UNASSIGNED_LOC
            ? `${API}/machinery/unassigned`
            : `${API}/locations/${locationChoice}/machinery`;
        const res = await axios.get(url);
        if (aborted) return;
        // Alphabetize equipment list by component name.
        setMachinery([...(res.data || [])].sort((a, b) =>
          (a.component || '').localeCompare(b.component || '')
        ));
      } catch (e) {
        if (!aborted) toast.error('Could not load equipment for this location');
      } finally {
        if (!aborted) setLoadingEquipment(false);
      }
    })();
    return () => { aborted = true; };
  }, [locationChoice]);

  // Look up the human-readable label the header on step 2 uses.
  const selectedLabels = useMemo(() => {
    const locName =
      locationChoice === UNASSIGNED_LOC
        ? 'Unassigned Equipment'
        : (locations.find((l) => l.id === locationChoice)?.name || '');
    let eqName = '';
    if (equipmentChoice === OTHER_EQUIPMENT) {
      eqName = otherEquipmentName.trim();
    } else if (!equipmentChoice && locationChoice === UNASSIGNED_LOC) {
      // Unassigned location + free-text equipment path.
      eqName = otherEquipmentName.trim();
    } else {
      eqName = machinery.find((m) => m.id === equipmentChoice)?.component || '';
    }
    return { locName, eqName };
  }, [
    locationChoice, locations,
    equipmentChoice, machinery, otherEquipmentName,
  ]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    Promise.all(files.map((f) => new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(f);
    }))).then(setImagePreviews);
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!locationChoice) return toast.error('Pick a plant location');
    // When location is Unassigned + there is no unassigned machinery tracked,
    // the equipment select stays empty and we collect a free-text label instead.
    const usingUnassignedFreeText =
      locationChoice === UNASSIGNED_LOC && !equipmentChoice && machinery.length === 0;
    if (!usingUnassignedFreeText) {
      if (!equipmentChoice) return toast.error('Pick equipment (or choose "Other — please specify")');
      if (equipmentChoice === OTHER_EQUIPMENT && !otherEquipmentName.trim()) {
        return toast.error('Describe the equipment');
      }
    } else if (!otherEquipmentName.trim()) {
      return toast.error('Describe the equipment');
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error('Description is required');
    setSubmitting(true);
    try {
      const imageUrls = [];
      for (const f of imageFiles) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/upload-image`, fd);
        imageUrls.push(r.data.image_url);
      }
      const observedISO = observedAt ? new Date(observedAt).toISOString() : null;
      await axios.post(`${API}/maintenance-requests`, {
        plant_location_id: locationChoice === UNASSIGNED_LOC ? null : locationChoice,
        equipment_name: selectedLabels.eqName,
        description: description.trim(),
        observed_at: observedISO,
        images: imageUrls,
      });
      toast.success('Maintenance request submitted');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="submit-request-dialog">
        <DialogHeader>
          <DialogTitle>Submit Maintenance Request</DialogTitle>
          <DialogDescription>
            Step {step} of 2 — {step === 1 ? 'where & what' : 'describe the issue'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={handleNext} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="req-location">Plant Location *</Label>
              <select
                id="req-location"
                data-testid="req-location-select"
                value={locationChoice}
                onChange={(e) => setLocationChoice(e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-white"
                required
              >
                <option value="">Choose a plant location…</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
                <option value={UNASSIGNED_LOC}>Unassigned Equipment</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-equipment">Equipment *</Label>
              <select
                id="req-equipment"
                data-testid="req-equipment-select"
                value={equipmentChoice}
                onChange={(e) => setEquipmentChoice(e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-white"
                disabled={!locationChoice || loadingEquipment}
                required
              >
                <option value="">
                  {loadingEquipment
                    ? 'Loading equipment…'
                    : machinery.length === 0 && locationChoice
                      ? (locationChoice === UNASSIGNED_LOC
                          ? 'No unassigned equipment tracked — describe it below'
                          : 'No equipment tracked here yet — pick Other below')
                      : 'Choose equipment…'}
                </option>
                {machinery.map((m) => (
                  <option key={m.id} value={m.id}>{m.component}</option>
                ))}
                {/* "Other" fallback is redundant when the operator has already
                    picked "Unassigned Equipment" as location — the concepts overlap
                    and confuse reporters. So it's hidden in that case. */}
                {locationChoice !== UNASSIGNED_LOC && (
                  <option value={OTHER_EQUIPMENT}>Other — please specify</option>
                )}
              </select>

              {locationChoice === UNASSIGNED_LOC && machinery.length === 0 && (
                <Input
                  data-testid="req-equipment-other-input"
                  value={otherEquipmentName}
                  onChange={(e) => setOtherEquipmentName(e.target.value)}
                  placeholder='Describe it (e.g. "untagged 1/2 inch ball valve near C-2")'
                  className="mt-2"
                  required
                />
              )}

              {equipmentChoice === OTHER_EQUIPMENT && (
                <Input
                  data-testid="req-equipment-other-input"
                  value={otherEquipmentName}
                  onChange={(e) => setOtherEquipmentName(e.target.value)}
                  placeholder='Describe it (e.g. "untagged 1/2 inch ball valve near C-2")'
                  className="mt-2"
                  required
                />
              )}
              <p className="text-xs text-slate-500">
                {locationChoice === UNASSIGNED_LOC
                  ? 'Picked "Unassigned Equipment" — describe the item so a tech can locate and track it.'
                  : (
                    <>
                      Pick the exact equipment from the list. If it isn&apos;t tagged, choose
                      &quot;Other — please specify&quot; and describe where the technician can find it.
                    </>
                  )}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" data-testid="req-step1-continue-btn">Continue →</Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-xs text-slate-600 bg-slate-100 rounded px-3 py-2" data-testid="req-summary-strip">
              Reporting <span className="font-medium">{selectedLabels.eqName}</span>
              {' '}at{' '}
              <span className="font-medium">{selectedLabels.locName}</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-description">Description of Issue *</Label>
              <Textarea
                id="req-description"
                data-testid="req-description-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder="What did you observe? Sound, smell, leak, vibration, gauge reading, etc."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-observed-at">Date & Time of Observation</Label>
              <Input
                id="req-observed-at"
                data-testid="req-observed-at-input"
                type="datetime-local"
                value={observedAt}
                onChange={(e) => setObservedAt(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-images">Photos (optional)</Label>
              <Input
                id="req-images"
                data-testid="req-images-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              {imagePreviews.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {imagePreviews.map((src, i) => (
                    <img key={i} src={imgSrc(src)} alt={`Preview ${i + 1}`} className="h-20 rounded border" />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={submitting}>
                ← Back
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="req-submit-btn">
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubmitMaintenanceRequestDialog;
